/**
 * PracticeModesStack — AI · Peers · Experts lane picker for community rails.
 * Keeps marketplace discoverable without polluting the top nav.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  People as PeopleIcon,
  Storefront as MarketplaceIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const MODES = {
  ai: {
    key: 'ai',
    label: 'Practice with AI',
    hint: 'Solo mock, instant feedback',
    icon: PlayArrowIcon,
    testId: 'practice-mode-ai',
  },
  peers: {
    key: 'peers',
    label: 'Practice with peers',
    hint: 'Connect and schedule a mock',
    icon: PeopleIcon,
    testId: 'practice-mode-peers',
  },
  experts: {
    key: 'experts',
    label: 'Book an interviewer',
    hint: 'Verified experts & alumni',
    icon: MarketplaceIcon,
    testId: 'practice-mode-experts',
  },
};

/**
 * @param {object} props
 * @param {(key: 'ai'|'peers'|'experts') => void} props.onSelect
 * @param {Array<'ai'|'peers'|'experts'>} [props.modes]
 * @param {'ai'|'peers'|'experts'|null} [props.emphasized] — filled primary treatment
 * @param {'ai'|'peers'|'experts'|null} [props.current] — shows “You’re here” hint
 * @param {string} [props.title]
 * @param {string} [props.subtitle]
 * @param {string} [props.testId]
 */
export default function PracticeModesStack({
  onSelect,
  modes = ['ai', 'peers', 'experts'],
  emphasized = 'ai',
  current = null,
  title = 'How do you want to practice?',
  subtitle = null,
  testId = 'practice-modes-stack',
}) {
  return (
    <Box data-testid={testId}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, letterSpacing: 0.04, textTransform: 'uppercase', display: 'block', mb: 0.5 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, lineHeight: 1.45 }}>
          {subtitle}
        </Typography>
      )}
      <Stack spacing={0.75} sx={{ mt: subtitle ? 0 : 0.5 }}>
        {modes.map((key) => {
          const mode = MODES[key];
          if (!mode) return null;
          const Icon = mode.icon;
          const isEmphasized = emphasized === key;
          const isCurrent = current === key;
          const hint = isCurrent ? 'You’re browsing peers' : mode.hint;

          return (
            <Box
              key={key}
              component="button"
              type="button"
              data-testid={mode.testId}
              onClick={() => onSelect(key)}
              disabled={isCurrent}
              aria-current={isCurrent ? 'page' : undefined}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: isEmphasized ? 'primary.main' : 'divider',
                bgcolor: isEmphasized
                  ? (t) => alpha(t.palette.primary.main, 0.08)
                  : 'background.paper',
                textAlign: 'left',
                cursor: isCurrent ? 'default' : 'pointer',
                font: 'inherit',
                color: 'inherit',
                opacity: isCurrent ? 0.85 : 1,
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': isCurrent
                  ? undefined
                  : {
                      borderColor: 'primary.light',
                      bgcolor: isEmphasized
                        ? (t) => alpha(t.palette.primary.main, 0.12)
                        : 'action.hover',
                    },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.25,
                  bgcolor: isEmphasized ? 'primary.main' : (t) => alpha(t.palette.primary.main, 0.08),
                  color: isEmphasized ? 'primary.contrastText' : 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {mode.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {hint}
                </Typography>
              </Box>
              {!isCurrent && (
                <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

PracticeModesStack.propTypes = {
  onSelect: PropTypes.func.isRequired,
  modes: PropTypes.arrayOf(PropTypes.oneOf(['ai', 'peers', 'experts'])),
  emphasized: PropTypes.oneOf(['ai', 'peers', 'experts', null]),
  current: PropTypes.oneOf(['ai', 'peers', 'experts', null]),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  testId: PropTypes.string,
};
